import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    dots: ['M12 12m-1 0a1 1 0 1 0 2 0 1 1 0 1 0-2 0', 'M12 5m-1 0a1 1 0 1 0 2 0 1 1 0 1 0-2 0', 'M12 19m-1 0a1 1 0 1 0 2 0 1 1 0 1 0-2 0'],
    up: ['M18 15l-6-6-6 6'],
    down: ['M6 9l6 6 6-6'],
    tripOperations: ['M3 11l19-9-9 19-2-8-8-2z']
};

export default function RouteStops({ user }) {
    const [routes, setRoutes] = useState([]);
    const [selectedRouteId, setSelectedRouteId] = useState('');
    const [stops, setStops] = useState([]);
    const [loadingStops, setLoadingStops] = useState(false);
    const [modal, setModal] = useState(null); // { type: 'add'|'edit', data }

    const operatorId = user?.operatorId || user?._id || 'all';

    const mapRef = useRef(null);
    const googleMap = useRef(null);
    const directionsRenderer = useRef(null);
    const stopMarkers = useRef([]);
    const stopsRef = useRef([]);
    const selectedRouteIdRef = useRef('');

    useEffect(() => { stopsRef.current = stops; }, [stops]);
    useEffect(() => { selectedRouteIdRef.current = selectedRouteId; }, [selectedRouteId]);

    useEffect(() => {
        fetchRoutes();
        initMap();
    }, []);

    const fetchRoutes = async () => {
        try {
            const res = await axios.get(`${API}/routes/${operatorId}`);
            setRoutes(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchStops = async (routeId) => {
        if (!routeId) {
            setStops([]);
            return;
        }
        setLoadingStops(true);
        try {
            const res = await axios.get(`${API}/route-stops/${routeId}`);
            setStops(res.data);
        } catch (err) { console.error(err); }
        finally { setLoadingStops(false); }
    };

    const initMap = () => {
        if (!window.google || !mapRef.current) return;
        googleMap.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 20.5937, lng: 78.9629 },
            zoom: 5,
            disableDefaultUI: true,
            zoomControl: true,
            styles: [
                { "featureType": "all", "elementType": "labels.text.fill", "stylers": [{ "color": "#747474" }] },
                { "featureType": "all", "elementType": "labels.text.stroke", "stylers": [{ "visibility": "off" }] }
            ]
        });

        directionsRenderer.current = new window.google.maps.DirectionsRenderer({
            map: googleMap.current,
            suppressMarkers: true,
            polylineOptions: { strokeColor: '#4f46e5', strokeWeight: 4 }
        });

        const geocoder = new window.google.maps.Geocoder();

        googleMap.current.addListener('click', (e) => {
            if (!selectedRouteIdRef.current) return;
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();

            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                let name = '';
                if (status === 'OK' && results[0]) {
                    name = results[0].formatted_address;
                }
                setModal({ type: 'add', data: { lat, lng, name, order: stopsRef.current.length } });
            });
        });
    };

    useEffect(() => {
        if (selectedRouteId) {
            fetchStops(selectedRouteId);
        } else {
            if (directionsRenderer.current) directionsRenderer.current.set('directions', null);
            setStops([]);
            clearMarkers();
        }
    }, [selectedRouteId]);

    useEffect(() => {
        if (selectedRouteId && routes.length > 0 && window.google) {
            const route = routes.find(r => r._id === selectedRouteId);
            if (route) {
                const ds = new window.google.maps.DirectionsService();

                // Add waypoints for each stop
                const waypoints = (stops || []).map(s => ({
                    location: { lat: s.lat, lng: s.lng },
                    stopover: true
                }));

                ds.route({
                    origin: route.pickupLocation,
                    destination: route.dropLocation,
                    waypoints: waypoints,
                    optimizeWaypoints: false, // Keep the manually defined order
                    travelMode: window.google.maps.TravelMode.DRIVING
                }, (res, status) => {
                    if (status === 'OK' && directionsRenderer.current) {
                        directionsRenderer.current.setDirections(res);
                    } else if (status === 'ZERO_RESULTS' || status === 'NOT_FOUND') {
                        // Fallback to just origin/destination if stops make it impossible
                        ds.route({
                            origin: route.pickupLocation,
                            destination: route.dropLocation,
                            travelMode: window.google.maps.TravelMode.DRIVING
                        }, (res2, status2) => {
                            if (status2 === 'OK' && directionsRenderer.current) {
                                directionsRenderer.current.setDirections(res2);
                            }
                        });
                    }
                });
            }
        } else if (!selectedRouteId) {
            if (directionsRenderer.current) directionsRenderer.current.set('directions', null);
        }
    }, [selectedRouteId, routes, stops]);

    useEffect(() => {
        updateMarkers();
    }, [stops]);

    const clearMarkers = () => {
        stopMarkers.current.forEach(m => m.setMap(null));
        stopMarkers.current = [];
    };

    const updateMarkers = () => {
        clearMarkers();
        if (!googleMap.current || !window.google) return;

        stops.forEach((stop, index) => {
            const marker = new window.google.maps.Marker({
                position: { lat: stop.lat, lng: stop.lng },
                map: googleMap.current,
                label: { text: (index + 1).toString(), color: 'white' },
                title: stop.name,
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: '#4f46e5',
                    fillOpacity: 1,
                    strokeColor: 'white',
                    strokeWeight: 2,
                    scale: 14
                }
            });

            marker.addListener('click', () => {
                setModal({ type: 'edit', data: stop });
            });

            stopMarkers.current.push(marker);
        });
    };

    const handleSaveStop = async (formData) => {
        try {
            const payload = {
                ...formData,
                lat: Number(formData.lat),
                lng: Number(formData.lng),
                routeId: selectedRouteId,
                operatorId
            };
            if (modal.type === 'edit') {
                await axios.put(`${API}/route-stops/${modal.data._id}`, payload);
            } else {
                await axios.post(`${API}/route-stops`, payload);
            }
            setModal(null);
            fetchStops(selectedRouteId);
        } catch (err) {
            console.error(err);
            alert(`Failed to save stop: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleDeleteStop = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            await axios.delete(`${API}/route-stops/${id}`);
            fetchStops(selectedRouteId);
        } catch (err) { console.error(err); }
    };

    const reorder = async (index, direction) => {
        const newStops = [...stops];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newStops.length) return;

        [newStops[index], newStops[targetIndex]] = [newStops[targetIndex], newStops[index]];
        setStops(newStops);

        const updates = newStops.map((s, i) => ({ _id: s._id, order: i }));
        try {
            await axios.put(`${API}/route-stops/reorder/bulk`, { stops: updates });
        } catch (err) { console.error(err); }
    };

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Route Stops Management</h1>
                    <p className="md-subtitle">Manage intermediate stops along your main transport routes.</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(400px, 1fr) 1.5fr', gap: '24px', marginTop: '20px' }}>
                <div className="md-panel" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
                    <div className="md-form-group" style={{ padding: '16px' }}>
                        <label className="md-label">Select Main Route</label>
                        <select
                            className="md-input"
                            value={selectedRouteId}
                            onChange={e => setSelectedRouteId(e.target.value)}
                        >
                            <option value="">-- Choose a Route --</option>
                            {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                        </select>
                    </div>

                    {selectedRouteId && (
                        <>
                            <div className="md-panel-toolbar" style={{ borderTop: '1px solid #f1f5f9', padding: '12px 16px' }}>
                                <span style={{ fontWeight: 600, fontSize: '14px' }}>Route Stops ({stops.length})</span>
                                <button className="md-add-btn" onClick={() => setModal({ type: 'add', data: { name: '', order: stops.length } })} style={{ padding: '6px 12px', fontSize: '12px' }}>
                                    <Icon d={icons.plus} size={12} /> Add Stop
                                </button>
                            </div>

                            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                                {loadingStops ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading stops...</div>
                                ) : modal ? (
                                    <InlineStopForm
                                        key={modal.data?._id || `new-${modal.data?.lat}-${modal.data?.lng}`}
                                        type={modal.type}
                                        data={modal.data}
                                        onClose={() => setModal(null)}
                                        onSave={handleSaveStop}
                                    />
                                ) : stops.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No stops added yet. Click on the map or use the button to add.</div>
                                ) : (
                                    <div className="stops-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {stops.map((stop, i) => (
                                            <div key={stop._id} className="stop-card" style={{
                                                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#fff',
                                                border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                transition: 'all 0.2s'
                                            }}>
                                                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600 }}>
                                                    {i + 1}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stop.name}</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>{stop.lat.toFixed(6)}, {stop.lng.toFixed(6)}</div>
                                                </div>
                                                <div className="stop-actions" style={{ display: 'flex', gap: '4px' }}>
                                                    <button className="md-icon-btn" onClick={() => reorder(i, -1)} disabled={i === 0} title="Move Up"><Icon d={icons.up} size={14} /></button>
                                                    <button className="md-icon-btn" onClick={() => reorder(i, 1)} disabled={i === stops.length - 1} title="Move Down"><Icon d={icons.down} size={14} /></button>
                                                    <button className="md-icon-btn" onClick={() => setModal({ type: 'edit', data: stop })} title="Edit"><Icon d={icons.edit} size={14} /></button>
                                                    <button className="md-icon-btn md-icon-btn-danger" onClick={() => handleDeleteStop(stop._id)} title="Delete"><Icon d={icons.trash} size={14} /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="map-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative' }}>
                    <div ref={mapRef} style={{ flex: 1, background: '#f1f5f9', borderRadius: '20px', minHeight: '600px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                    </div>
                    {!selectedRouteId && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', borderRadius: '20px' }}>
                            <div style={{ textAlign: 'center', padding: '32px', background: '#fff', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', maxWidth: '300px' }}>
                                <div style={{ background: '#eef2ff', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                    <Icon d={icons.tripOperations} size={32} style={{ color: '#4f46e5' }} />
                                </div>
                                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Select a Route</h3>
                                <p style={{ color: '#64748b', fontSize: '14px', marginTop: '8px' }}>Select a main route from the dropdown to start adding and managing stops.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function InlineStopForm({ type, data, onClose, onSave }) {
    const [form, setForm] = useState({
        name: data.name || '',
        lat: data.lat || '',
        lng: data.lng || '',
        order: data.order || 0
    });

    const autocompleteRef = useRef(null);

    useEffect(() => {
        if (!window.google) return;
        const autocomplete = new window.google.maps.places.Autocomplete(autocompleteRef.current, { fields: ['formatted_address', 'geometry', 'name'] });
        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.geometry) {
                setForm(prev => ({
                    ...prev,
                    name: place.formatted_address || place.name,
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng()
                }));
            }
        });
    }, []);

    return (
        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', animation: 'fadeIn 0.3s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: '#1e293b' }}>
                    {type === 'edit' ? 'Edit Stop' : 'Add New Stop'}
                </h3>
                <button onClick={onClose} style={{ border: 'none', background: 'none', padding: 4, cursor: 'pointer', color: '#64748b' }}>
                    <Icon d={icons.x} size={16} />
                </button>
            </div>

            <div className="md-form-group">
                <label className="md-label">Stop Location Search*</label>
                <div style={{ position: 'relative' }}>
                    <input
                        ref={autocompleteRef}
                        className="md-input"
                        placeholder="Search for a location..."
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        style={{ paddingLeft: '32px' }}
                    />
                    <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                        <Icon d={icons.search} size={14} />
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div className="md-form-group">
                    <label className="md-label">Latitude*</label>
                    <input className="md-input" type="number" step="any" value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })} placeholder="Lat" />
                </div>
                <div className="md-form-group">
                    <label className="md-label">Longitude*</label>
                    <input className="md-input" type="number" step="any" value={form.lng} onChange={e => setForm({ ...form, lng: e.target.value })} placeholder="Lng" />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                <button
                    className="md-cancel-btn"
                    onClick={onClose}
                    style={{ flex: 1, padding: '8px', fontSize: '13px' }}
                >
                    Cancel
                </button>
                <button
                    className="md-save-btn"
                    onClick={() => onSave(form)}
                    disabled={!form.name || form.lat === '' || form.lng === ''}
                    style={{ flex: 2, padding: '8px', fontSize: '13px', opacity: (!form.name || form.lat === '' || form.lng === '') ? 0.7 : 1 }}
                >
                    {type === 'edit' ? 'Update Stop' : 'Save Stop'}
                </button>
            </div>
        </div>
    );
}
